// pages/FormViewerPage/sections/FullFormBody.tsx — Body form lengkap (checkout)
import React from 'react';
import CustomerInfoFields from '../components/CustomerInfoFields';
import VariantOptions from '../components/VariantOptions';
import { FadeInBlock } from '../components/FadeInBlock';
import { resolveVariantImageUrl } from '../../../utils/variantImage';
import { ProductImageSection } from './ProductImageSection';
import { TestimonialsSection } from './TestimonialsSection';
import { FormHeaderSection } from './FormHeaderSection';
import { ShippingMethodSection } from './ShippingMethodSection';
import { PaymentMethodSection } from './PaymentMethodSection';
import { OrderSummarySection } from './OrderSummarySection';
import { TrustBadgesFooterSection } from './TrustBadgesFooterSection';
import { HerbalShippingClaimCard } from '../components/HerbalShippingClaimCard';
import StageGate from '../../../components/StageGate';
import RealSocialProofToast from '../../../components/RealSocialProof/Toast';
import { BumpUpsell } from './BumpUpsell';
import type { FormViewerBodyProps } from './FormViewerBody/types';

export const FullFormBody: React.FC<FormViewerBodyProps> = ({
    hook, form, isEmbed, isSeamless, imageLoaded, setImageLoaded, subtotal,
    shippingKeys, blockSpacingClass, descriptionClasses, customerData, fieldErrors,
    addressData, setAddressData, selectedOptions, setSelectedOptions, displayOptions,
    compositeFallback, currentCombination, currentVariantStock, resolvedVariantCombinations,
    EXCLUDED_PROVINCES, getFlatRegionKey, forceShowPostalCode,
    handleCustomerDataChange, handleWhatsappChange, handleSubmit,
}) => {
    const shippingHook = { ...hook, subtotal, shippingKeys };
    // Mode Konsultasi = lead-only (nama + WA saja). Pertahanan lapis-2 kalau jalur
    // full ter-render: variant + ongkir/bayar disembunyikan, field cukup nama + WA.
    const isConsult = !!form?.thankYouPage?.consultationMode;

    const sp = (form as any)?.socialProofSettings;
    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="relative">
            {!isEmbed && sp?.active && <RealSocialProofToast formId={(form as any)?.id} active initialDelayMs={(sp?.initialDelaySeconds ?? 3) * 1000} displayMs={(sp?.displayDurationSeconds ?? 4) * 1000} intervalMs={(sp?.intervalSeconds ?? 8) * 1000} />}
            <div className={isSeamless ? 'bg-transparent p-1 text-slate-900 dark:text-slate-100' : 'bg-white dark:bg-slate-800 p-4 sm:p-6 lg:p-6 xl:p-6 text-slate-900 dark:text-slate-100'}>
                {!isEmbed && (
                    <ProductImageSection form={form} imageOverride={currentCombination?.imageUrl || resolveVariantImageUrl(form?.variantCombinations as any, selectedOptions, (form as any)?.productVariants) || null} setImageLoaded={setImageLoaded} blockSpacingClass={blockSpacingClass} />
                )}

                {!isEmbed && !isConsult && (
                    <StageGate mode="view" minHeight={180}>
                        <TestimonialsSection form={form} blockSpacingClass={blockSpacingClass} />
                    </StageGate>
                )}

                <FormHeaderSection form={form} blockSpacingClass={blockSpacingClass} descriptionClasses={descriptionClasses} />

                {!isConsult && (
                    <VariantOptions displayOptions={displayOptions} blockSpacingClass={blockSpacingClass} selectedOptions={selectedOptions}
                        fieldErrors={fieldErrors}
                        onOptionChange={(name, value) => setSelectedOptions(prev => ({ ...prev, [name]: value }))}
                        resolvedVariantCombinations={resolvedVariantCombinations} compositeFallback={compositeFallback}
                        variantStock={hook.variantStock} currentVariantStock={currentVariantStock} form={form} />
                )}

                <CustomerInfoFields form={form} customerData={customerData} fieldErrors={fieldErrors}
                    addressData={addressData} onAddressDataChange={setAddressData}
                    onCustomerDataChange={handleCustomerDataChange} onWhatsappChange={handleWhatsappChange}
                    blockSpacingClass={blockSpacingClass} getFlatRegionKey={getFlatRegionKey}
                    EXCLUDED_PROVINCES={EXCLUDED_PROVINCES} forceShowPostalCode={forceShowPostalCode}
                    isSimpleEmbed={isConsult} />
                    {form.addressInputMode === 'simple' && (
                        <HerbalShippingClaimCard
                            form={form}
                            claimMode={hook.herbalClaimMode || 'wa'}
                            onClaimModeChange={hook.setHerbalClaimMode}
                            addressData={addressData}
                            onAddressChange={setAddressData}
                            fieldErrors={fieldErrors}
                        />
                    )}

                {!isConsult && (hook?.bumpOptions?.length > 0) && (
                    <BumpUpsell options={hook.bumpOptions} selectedIds={hook.selectedBumpIds || []} onToggle={hook.toggleBump} />
                )}

                {/* Perf 2026-10-11: blok kritis (ongkir/bayar/ringkasan) WAJIB
                    visible segera — delay 600/750ms bikin form terasa lama
                    kebuka di HP. Animasi fade tetap jalan (delay 0). */}
                {!isConsult && (
                    <FadeInBlock delay={0}>
                        <ShippingMethodSection hook={shippingHook} blockSpacingClass={blockSpacingClass} subtotal={subtotal} shippingKeys={shippingKeys} onSettledState={(s) => { if (hook?.ratesSettledRef) hook.ratesSettledRef.current = s; }} />
                        <PaymentMethodSection hook={shippingHook} blockSpacingClass={blockSpacingClass} subtotal={subtotal} />
                    </FadeInBlock>
                )}

                <FadeInBlock delay={0}>
                    <OrderSummarySection hook={shippingHook} subtotal={subtotal} />
                </FadeInBlock>

                <StageGate mode="view" minHeight={120}>
                    <TrustBadgesFooterSection form={form} />
                </StageGate>
            </div>
        </form>
    );
};
